import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTrackActivatedSearch, useTrackSearchSuggestionClicked } from "@air/analytics";
import { Combobox } from "@reach/combobox";
import debounce from "lodash/debounce";
import dynamic from "next/dynamic";
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { usePrevious } from "__barrel_optimize__?names=usePrevious!=!react-use";
import { SearchInput } from "~/components/SearchBar/SearchInput";
import { SearchSuggestions } from "~/components/SearchBar/SearchSuggestions";
import { BOARD_UNIQ_CHAR_REGEXP } from "~/components/SearchBar/Suggestion/BoardSuggestion";
import { SEARCH_CONTAINER } from "~/constants/testIDs";
import { isMobileAgent } from "~/utils/PlatformHelpers";
const SearchShortcutsInfo = dynamic(()=>import("~/components/SearchBar/SearchShortcutsInfo").then((mod)=>mod.SearchShortcutsInfo), {
    loadableGenerated: {
        webpack: ()=>[
                require.resolveWeak("~/components/SearchBar/SearchShortcutsInfo")
            ]
    },
    ssr: false
});
const _SearchBar = /*#__PURE__*/ forwardRef((param, forwardedRef)=>{
    let { searchTerm = "", onApplyTextSearch, onCloseClick, onSearchTermChange, handleSuggestionEnterClick, onFocusChange, placeholder, trackLocation, children, onBackspacePress, onSearchClear, renderSuggestions, hasSuggestions } = param;
    const inputRef = useRef(null);
    const { trackSearchSuggestionClicked } = useTrackSearchSuggestionClicked();
    const { trackActivatedSearch } = useTrackActivatedSearch();
    const [isFocused, setIsFocused] = useState(false);
    const [value, setValue] = useState("");
    const previousSearchTerm = usePrevious(searchTerm);
    useImperativeHandle(forwardedRef, ()=>({
            focus: ()=>{
                var _inputRef_current;
                return (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus();
            }
        }));
    // eslint complains about passing inline function, but only this version works with debounce
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedOnSearchChange = useCallback(debounce(onSearchTermChange, 300), [
        onSearchTermChange
    ]);
    const setSearchValue = useCallback((text)=>{
        setValue(text);
        debouncedOnSearchChange(text);
    }, [
        debouncedOnSearchChange
    ]);
    /**
     * If a user navigates to a search result page on a public bar, on the server side,
     * searchTerm will be empty but it will have a value client side on first render. To
     * avoid a rehydration issue, we sync the two of them initially on mount AFTER the initial render.
     */ useEffect(()=>{
        // when component is mounted, searchTerm is not ready - it is read from url params from redux, which may not be there yet
        // so later on, when searchTerm is ready, we set it to the value
        if (searchTerm !== previousSearchTerm) {
            setSearchValue(searchTerm !== null && searchTerm !== void 0 ? searchTerm : "");
        }
    }, [
        previousSearchTerm,
        searchTerm,
        setSearchValue
    ]);
    const _onSearchTermChange = useCallback((event)=>{
        const textValue = event.target.value;
        setSearchValue(textValue.replace(BOARD_UNIQ_CHAR_REGEXP, ""));
    }, [
        setSearchValue
    ]);
    const onSearchClearClick = useCallback(()=>{
        var _inputRef_current;
        setSearchValue("");
        onSearchClear === null || onSearchClear === void 0 ? void 0 : onSearchClear();
        (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus();
    }, [
        onSearchClear,
        setSearchValue
    ]);
    const onSearchCloseClick = useCallback(()=>{
        var _inputRef_current;
        setSearchValue("");
        onCloseClick();
        (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.blur();
    }, [
        onCloseClick,
        setSearchValue
    ]);
    const onSuggestionClick = useCallback((text, suggestionType)=>{
        trackSearchSuggestionClicked({
            text,
            type: suggestionType
        });
        setSearchValue("");
    }, [
        setSearchValue,
        trackSearchSuggestionClicked
    ]);
    const onApplySearch = useCallback((text)=>{
        var _inputRef_current;
        trackSearchSuggestionClicked({
            text,
            type: "text"
        });
        setSearchValue(text);
        (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.blur();
        onApplyTextSearch(text);
    }, [
        setSearchValue,
        onApplyTextSearch,
        trackSearchSuggestionClicked
    ]);
    useHotkeys("meta+p,ctrl+p", (event)=>{
        if (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) {
            var _inputRef_current;
            event.preventDefault();
            inputRef === null || inputRef === void 0 ? void 0 : (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus();
            trackActivatedSearch({
                location: trackLocation,
                isQuickSearch: true
            });
        }
    }, [
        onSearchCloseClick
    ]);
    // onSelect has selected text as the argument, but we do no utilize it - we do not know if it was text or board selection
    // Board titles have added U+0000 (NULL) added to titles to distinguish between boards with the same name
    const onSelect = useCallback(()=>{
        // without setTimeout blur does not work - Combobox is trying to refocus input after select
        setTimeout(()=>{
            var _inputRef_current;
            (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.blur();
        });
    }, []);
    // Combobox sends only information about selected text, thus we do not know if user selected text suggestion or a board
    // As a workaround, board rows has data-boardid set. If user clicks enter, we have to check if highlighted row has this attribute
    // if so, select a board. Otherwise, this is text selection
    const _onSuggestionEnterClick = useCallback((text)=>{
        const suggestions = document.querySelectorAll("[data-reach-combobox-list] > [data-highlighted]");
        const handled = handleSuggestionEnterClick === null || handleSuggestionEnterClick === void 0 ? void 0 : handleSuggestionEnterClick(text, suggestions[0]);
        if (!handled) {
            onApplySearch(text);
        }
    }, [
        handleSuggestionEnterClick,
        onApplySearch
    ]);
    const _onFocusChange = useCallback((isFocused)=>{
        setIsFocused(isFocused);
        onFocusChange === null || onFocusChange === void 0 ? void 0 : onFocusChange(isFocused);
    }, [
        onFocusChange
    ]);
    const onSearchActivated = useCallback(()=>{
        trackActivatedSearch({
            location: trackLocation,
            isQuickSearch: false
        });
    }, [
        trackLocation,
        trackActivatedSearch
    ]);
    return /*#__PURE__*/ _jsx("div", {
        className: "-mb-2 h-10 flex-1 md:h-12",
        "data-testid": SEARCH_CONTAINER,
        children: /*#__PURE__*/ _jsxs(Combobox, {
            openOnFocus: true,
            onSelect: onSelect,
            children: [
                /*#__PURE__*/ _jsx(SearchInput, {
                    onSearchActivated: onSearchActivated,
                    onBackspacePress: onBackspacePress,
                    placeholder: placeholder,
                    onEnterPress: _onSuggestionEnterClick,
                    onFocusChange: _onFocusChange,
                    ref: inputRef,
                    value: value,
                    hasSuggestions: hasSuggestions,
                    onChange: _onSearchTermChange,
                    onClearClick: onSearchClearClick,
                    onCloseClick: onSearchCloseClick,
                    shortcutsInfo: isMobileAgent ? null : /*#__PURE__*/ _jsx(SearchShortcutsInfo, {}),
                    children: children
                }),
                isFocused && hasSuggestions && /*#__PURE__*/ _jsx(SearchSuggestions, {
                    children: renderSuggestions({
                        applyTextSearch: onApplySearch,
                        onClick: onSuggestionClick,
                        value
                    })
                })
            ]
        })
    });
});
_SearchBar.displayName = "_SearchBar";
export const SearchBar = /*#__PURE__*/ memo(_SearchBar);
SearchBar.displayName = "SearchBar";
